import { useCallback, useEffect, type RefObject } from 'react';

interface UseDropdownKeyboardNavigationProps {
  isOpen: boolean;
  buttonRef: RefObject<HTMLButtonElement>;
  dropdownRef: RefObject<HTMLDivElement>;
  itemRefs: RefObject<(HTMLAnchorElement | null)[]>;
  toggleDropdown: () => void;
  setIsOpen: (isOpen: boolean) => void;
}

export const useDropdownKeyboardNavigation = ({
  isOpen,
  buttonRef,
  dropdownRef,
  itemRefs,
  toggleDropdown,
  setIsOpen,
}: UseDropdownKeyboardNavigationProps) => {
  const handleEscape = useCallback(() => {
    setIsOpen(false);
    buttonRef.current?.focus();
  }, [setIsOpen, buttonRef]);

  const isEventWithinDropdown = useCallback(
    (event: KeyboardEvent): boolean => {
      const target = event.target as Node;
      return (
        (dropdownRef.current?.contains(target) ||
          itemRefs.current?.some((ref) => ref?.contains(target)) ||
          target === buttonRef.current) ??
        false
      );
    },
    [buttonRef, dropdownRef, itemRefs]
  );

  const handleTabNavigation = useCallback(
    (event: KeyboardEvent) => {
      if (event.target === buttonRef.current) return;

      event.preventDefault();
      const focusableItems =
        itemRefs.current?.filter((ref): ref is HTMLAnchorElement => ref !== null) || [];
      if (focusableItems.length === 0) return;

      const currentIndex = focusableItems.findIndex((ref) => ref === document.activeElement);

      const handleShiftTab = (focusableItems: HTMLAnchorElement[], currentIndex: number) => {
        if (currentIndex <= 0) {
          buttonRef.current?.focus();
        } else {
          focusableItems[currentIndex - 1]?.focus();
        }
      };

      const handleTab = (focusableItems: HTMLAnchorElement[], currentIndex: number) => {
        if (currentIndex >= focusableItems.length - 1) {
          setIsOpen(false);
          buttonRef.current?.focus();
        } else {
          const nextIndex = currentIndex === -1 ? 0 : currentIndex + 1;
          focusableItems[nextIndex]?.focus();
        }
      };

      if (event.shiftKey) {
        handleShiftTab(focusableItems, currentIndex);
      } else {
        handleTab(focusableItems, currentIndex);
      }
    },
    [buttonRef, itemRefs, setIsOpen]
  );

  // Handle button keyboard events
  useEffect(() => {
    const handleButtonKeyDown = (event: KeyboardEvent) => {
      // Only handle if button is focused and dropdown is closed
      if (
        event.target === buttonRef.current &&
        !isOpen &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        toggleDropdown();
      }

      // Handle Tab from button when dropdown is open
      const isButtonFocused = event.target === buttonRef.current;
      const isTabForward = event.key === 'Tab' && !event.shiftKey;

      if (isButtonFocused && isOpen && isTabForward) {
        event.preventDefault();
        const firstItem = itemRefs.current?.find((ref) => ref !== null);
        firstItem?.focus();
      }
    };

    document.addEventListener('keydown', handleButtonKeyDown);
    return () => document.removeEventListener('keydown', handleButtonKeyDown);
  }, [isOpen, toggleDropdown, buttonRef, itemRefs]);

  // Handle dropdown navigation (Escape and Tab management)
  useEffect(() => {
    if (!isOpen) return;

    const handleDropdownKeyDown = (event: KeyboardEvent) => {
      if (!isEventWithinDropdown(event)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        handleEscape();
        return;
      }

      if (event.key === 'Tab') {
        handleTabNavigation(event);
      }
    };

    document.addEventListener('keydown', handleDropdownKeyDown);
    return () => document.removeEventListener('keydown', handleDropdownKeyDown);
  }, [isOpen, handleTabNavigation, handleEscape, isEventWithinDropdown]);

  const handleItemKeyDown = useCallback(
    (event: React.KeyboardEvent, index: number) => {
      // Handle Enter/Space activation for individual navigation links
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        itemRefs.current?.[index]?.click();
      }
    },
    [itemRefs]
  );

  return { handleItemKeyDown };
};
